import { Link, styled, Button, Box } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { FINANCIAL_USERS_ACCESS_KEYS, OKTA_STATUS_URL } from '../../../common/constants';
import { useAppDispatch, useAppSelector } from '../../../common/hooks';
import { Toast } from '../../molecules/Toast/Toast';
import ActiveSwitch from './ActiveSwitch';
import {
    fetchAdminsAndAdvisors,
    resetPassword,
    updateUser,
    activateUser,
    deactivateUser,
    updateUsersByEmail,
    updateFinancialAdvisorDeleteModalState
} from '../../../features/adviceAdmin';
import FinancialAdvisor from '../FinancialAdvisors';
import { updateGlobalLoaderState } from '../../../features/global/globalSlice';
import { debounce } from '../../../utils/index';
import { UserRoleDropDown } from './UserRoleDropDown';
import { OktaStatusCell } from './OktaStatusCell';
import TwoWayTransferPlansModal from './TwoWayTransferPlansModal';
import { Pagination } from '../../atoms/Pagination';
import PlanTransferApprovals from '../../../containers/plan-transfer-approvals';
import { usePlanTransferApprovals } from '../../../containers/plan-transfer-approvals/hooks';
import { ConfirmationCentered } from '../../molecules/ConfirmationDialog/ConfirmationDialog';

const StyledBox = styled(Box)(() => ({
    border: '1px solid #E6E6E6',
    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.05)',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: 'background.paper',
    width: '100%',
    p: 2,
    margin: '0 0 20px 0'
}));

const StyledEmail: any = styled('div')(() => ({
    display: 'flex',
    maxWidth: '700px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden'
}));

const StyledUnderlinedLink = styled(Link)(() => ({
    textDecoration: 'underline',
    cursor: 'pointer'
}));

const OktaButton = styled(Button)({
    textTransform: 'none',
    '&:hover': { color: '#04509dff', textDecoration: 'none' },
    fontWeight: '700',
    fontSize: 14
});

const TransferBox = styled(Box)({
    transform: 'translateY(-10px)'
});

interface fianancialAdvisorsProps {
    userRole: any;
    userTenant: any;
    isTenantAdmin?: boolean;
}
const RelativeContainer = styled(Box)(() => ({
    position: 'relative',
    minHeight: '250px'
}));

const PaginationContainer = styled('div')(() => ({
    display: 'flex',
    justifyContent: 'end',
    marginTop: '1rem'
}));

export default function FinancialAdvisors({ userRole, userTenant, isTenantAdmin }: fianancialAdvisorsProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { pendingApprovals, approve, reject, isLoading, actionLoading, snackbar, refreshApprovals } =
        usePlanTransferApprovals();
    // const navigate = useNavigate();
    // const tenant = useAppSelector((state: any) => state?.global?.globalConfig?.subdomain);
    const tenant = useAppSelector((state: any) => state?.auth?.user?.tenant);
    const financialUsers = useAppSelector((state) => state.advisorAdmin.users);
    const showFinancialAdvisorRemoveModal = useAppSelector(
        (state) => state.advisorAdmin.showFinancialAdvisorRemoveModal
    );
    const advisorEmailId = useAppSelector((state) => state.advisorAdmin.advisorEmailId);
    const [localLoader, setLocalLoader] = useState<boolean>(false);
    const [resetPasswordMailed, setResetPasswordMailed] = useState<boolean>(false);
    const [toastMessege, setToastMessege] = useState<string>('');
    const [toastHeader, setToastHeader] = useState<string>('');
    const [hasServerError, setHasServerError] = useState({ name: '', message: '', statusCode: 200 });
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [transferModalOpen, setTransferModalOpen] = useState<boolean>(false);
    const [deleteModalState, setDeleteModalState] = useState(false);
    const dataExists = useAppSelector((state: any) => state.advisorAdmin?.adminsAndAdvisors?.length > 0);
    const admin_tenantListFromApi = useAppSelector((state) => state?.auth?.user?.admin_tenantList);

    const totalPages = useAppSelector((state) => state.advisorAdmin.totoalPages);
    const totalUserCount = useAppSelector((state) => state.advisorAdmin.totalUserCount);
    const [curentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [filterRole, setFilterRole] = useState('allroles');
    const [confirmResetPasswordModalOpen, setConfirmResetPasswordModalOpen] = useState(false);
    const [userToResetPassword, setUserToResetPassword] = useState<any>(null);

    useEffect(() => {
        setLocalLoader(true);
        dispatch(fetchAdminsAndAdvisors({ page: curentPage, page_size: itemsPerPage })).finally(() => {
            setLocalLoader(false);
        });
    }, [dataExists, dispatch]);

    useEffect(() => {
        setDeleteModalState(showFinancialAdvisorRemoveModal as any);
    }, [advisorEmailId, showFinancialAdvisorRemoveModal]);

    const handleCloseDeleteModal = () => {
        dispatch(updateFinancialAdvisorDeleteModalState(false));
    };

    // pagination
    const handlePageChange = (page: number) => {
        setLocalLoader(true);
        dispatch(
            fetchAdminsAndAdvisors({
                search_term: searchTerm,
                page: page,
                page_size: itemsPerPage,
                selected_role: filterRole
            })
        ).finally(() => {
            setLocalLoader(false);
            setCurrentPage(page);
        });
    };

    const handlePageSizeChange = (pageSize: number) => {
        setLocalLoader(true);
        dispatch(
            fetchAdminsAndAdvisors({ search_term: searchTerm, page: 1, page_size: pageSize, selected_role: filterRole })
        ).finally(() => {
            setLocalLoader(false);
            setCurrentPage(1);
            setItemsPerPage(pageSize);
        });
    };

    const searchAdvisors = (query: string, role: string = 'allroles') => {
        setLocalLoader(true);
        dispatch(
            fetchAdminsAndAdvisors({ search_term: query, page: 1, page_size: itemsPerPage, selected_role: role })
        ).finally(() => {
            setCurrentPage(1);
            setLocalLoader(false);
        });
    };

    const debouncedSearch = useMemo(
        () =>
            debounce((query: string, role: string) => {
                searchAdvisors(query, role);
            }, 1000),
        []
    );
    const handleTextInput = (value: string) => {
        setSearchTerm(value);
        debouncedSearch(value, filterRole);
    };

    const handleRoleFilterChange = (role: string) => {
        setFilterRole(role);
        searchAdvisors(searchTerm, role);
    };

    const handleRoleChange = async (user: any) => {
        dispatch(updateGlobalLoaderState(true));
        try {
            const res: any = await dispatch(
                updateUser({
                    firstName: user.userName,
                    phoneNumber: user.userPhoneNumber,
                    role: user.userType,
                    tenant: user.userTenant,
                    id: user?.id,
                    createdOn: '-',
                    okta_login_status: user?.okta_login_status
                })
            );
            if (res?.payload?.success) {
                dispatch(
                    updateUsersByEmail({
                        userEmail: user.userEmail,
                        userType: user.userType,
                        userActive: user.userActive
                    })
                );
                setToastMessege('ROLE_CHANGED_SUCCESSFULLY_MESSEGE');
                setToastHeader('ROLE_CHANGED_SENT_SUCCESSFULLY_HEADER');
                setResetPasswordMailed(true);
            }

            if (res.payload?.status === 400 || res.payload?.status === 500) {
                setHasServerError({
                    message: res.payload?.response.data.message,
                    name: res.payload?.response.data.name,
                    statusCode: res.payload?.status
                });
            }
        } finally {
            dispatch(updateGlobalLoaderState(false));
        }
    };

    const handleCloseResetModal = () => {
        setConfirmResetPasswordModalOpen(false);
        setUserToResetPassword(null);
    };

    const handleResetPassword = async (user: any) => {
        handleCloseResetModal();
        const res: any = await dispatch(resetPassword({ userName: user?.userEmail, tenant: user?.userTenant }));

        if (res?.payload?.success) {
            // reset();
            // setHasServerError({ message: '', name: '', statusCode: 200 });
            setToastMessege('RESET_PASSWORD_SENT_SUCCESSFULLY_MESSEGE');
            setToastHeader('RESET_PASSWORD_SENT_SUCCESSFULLY_HEADER');
            setResetPasswordMailed(true);
        }

        if (res.payload?.status === 400 || res.payload?.status === 500) {
            setHasServerError({
                message: res.payload?.response.data.message,
                name: res.payload?.response.data.name,
                statusCode: res.payload?.status
            });
        }
    };

    const handleActiveInactive = async (user: any, flag: boolean) => {
        let res: any = null;
        if (flag) {
            res = await dispatch(activateUser({ userId: user?.id, tenant: user?.userTenant }));
        } else {
            res = await dispatch(deactivateUser({ userId: user?.id, tenant: user?.userTenant }));
        }
        if (res?.payload?.success) {
            // reset();
            // setHasServerError({ message: '', name: '', statusCode: 200 });
            dispatch(updateUsersByEmail({ userEmail: user.userEmail, userType: user.userType, userActive: flag }));
            setToastMessege(flag ? t('ACTIVE_STATUS_MESSEGE') : t('INACTIVE_STATUS_MESSEGE'));
            setToastHeader(t('INACTIVE_STATUS_HEADER'));
            setResetPasswordMailed(true);
        }

        if (res.payload?.status === 400 || res.payload?.status === 500) {
            setHasServerError({
                message: res.payload?.response.data.message,
                name: res.payload?.response.data.name,
                statusCode: res.payload?.status
            });
        }
    };

    const handleRemoveFinancialAdvisor = async () => {
        setLocalLoader(true);

        const userId: string = (showFinancialAdvisorRemoveModal as any).id;
        await dispatch(deactivateUser({ userId, tenant }));
        setDeleteModalState(false);
        dispatch(updateFinancialAdvisorDeleteModalState(false));

        setLocalLoader(false);
    };

    const closeToast = () => {
        setResetPasswordMailed(false);
    };

    const getTenantLabel = (tenant: string) => {
        const foundTenant = admin_tenantListFromApi?.find(
            (item: { value: string; label: string }) => item.value === tenant
        );
        return foundTenant ? foundTenant.label : tenant;
    };

    const isFtAdmin = userRole?.toLowerCase() === 'ftadmin';
    const columns = useMemo(() => {
        return [
            {
                accessorKey: FINANCIAL_USERS_ACCESS_KEYS.USER_NAME,
                id: FINANCIAL_USERS_ACCESS_KEYS.USER_NAME,
                header: t('FINANCIAL_ADMIN_TABLE_USER_NAME_COLUMN'),
                // cell: ({ getValue }: any) => getValue()
                cell: ({ row }: any) => `${row.original.userName}`
            },
            {
                accessorKey: FINANCIAL_USERS_ACCESS_KEYS.USER_EMAIL,
                id: FINANCIAL_USERS_ACCESS_KEYS.USER_EMAIL,
                header: t('FINANCIAL_ADMIN_TABLE_USER_EMAIL_COLUMN'),
                cell: ({ getValue }: any) => <StyledEmail>{getValue()}</StyledEmail>
            },
            ...(isFtAdmin ? [{
                accessorKey: FINANCIAL_USERS_ACCESS_KEYS.TENANT_NAME,
                id: FINANCIAL_USERS_ACCESS_KEYS.TENANT_NAME,
                header: t('FINANCIAL_ADMIN_TABLE_USER_TENANT_COLUMN'),
                cell: ({ getValue }: any) => getTenantLabel(getValue())
            }] : []),
            {
                accessorKey: FINANCIAL_USERS_ACCESS_KEYS.USER_TYPE,
                id: FINANCIAL_USERS_ACCESS_KEYS.USER_TYPE,
                header: t('FINANCIAL_ADMIN_TABLE_USER_TYPE_COLUMN'),
                cell: ({ row }: any) => (
                    <UserRoleDropDown
                        updateUserRole={handleRoleChange}
                        selectedRole={row.original.userType}
                        userRole={userRole}
                        useData={row.original}
                    />
                )
            },
            {
                accessorKey: FINANCIAL_USERS_ACCESS_KEYS.RESET_PASSWORD,
                id: FINANCIAL_USERS_ACCESS_KEYS.RESET_PASSWORD,
                header: t('FINANCIAL_ADMIN_TABLE_RESET_PASSWORD_COLUMN'),
                enableSorting: false,
                cell: ({ row }: any) => (
                    <StyledUnderlinedLink
                        onClick={() => {
                            setUserToResetPassword(row.original);
                            setConfirmResetPasswordModalOpen(true);
                        }}
                    >
                        {t('FINANCIAL_ADMIN_TABLE_RESET_PASSWORD_COLUMN')}
                    </StyledUnderlinedLink>
                )
            },
            {
                accessorKey: FINANCIAL_USERS_ACCESS_KEYS.ACCOUNT_STATE,
                id: FINANCIAL_USERS_ACCESS_KEYS.ACCOUNT_STATE,
                header: t('FINANCIAL_ADMIN_TABLE_STATUS_COLUMN'),
                enableSorting: false,
                cell: ({ row }: any) => (
                    <ActiveSwitch handleActiveInactive={handleActiveInactive} userData={row.original} />
                )
            },
            ...(isFtAdmin ? [{
                accessorKey: FINANCIAL_USERS_ACCESS_KEYS.USER_STATUS,
                id: FINANCIAL_USERS_ACCESS_KEYS.USER_STATUS,
                header: () => (
                    <OktaButton
                        variant="text"
                        size="small"
                        onClick={() => window.open(OKTA_STATUS_URL, '_blank', 'noopener,noreferrer')}
                    >
                        {t('OKTA_API_STATUS_COLUMN')} <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5 }} />
                    </OktaButton>
                ),
                enableSorting: false,
                cell: OktaStatusCell
            }] : []),
            {
                accessorKey: FINANCIAL_USERS_ACCESS_KEYS.PLAN_TRANSFER,
                id: FINANCIAL_USERS_ACCESS_KEYS.PLAN_TRANSFER,
                header: t('FINANCIAL_ADMIN_TABLE_PLAN_TRANSFER_COLUMN'),
                enableSorting: false,
                cell: ({ row }: any) => {
                    const user = row.original;
                    return (
                        <TransferBox>
                            <StyledUnderlinedLink
                                onClick={async () => {
                                    setSelectedUser(user);
                                    setTransferModalOpen(true);
                                }}
                            >
                                {t('FINANCIAL_ADMIN_TABLE_PLAN_TRANSFER_ACTION')}
                            </StyledUnderlinedLink>
                        </TransferBox>
                    );
                }
            }
        ];
    }, [userRole, t]);

    return (
        <>
            {resetPasswordMailed && (
                <Toast
                    heading={t(toastHeader)}
                    type="confirm"
                    disappearTime={4}
                    messages={[`${t(toastMessege)}`]}
                    isTimer
                    handleClose={closeToast}
                />
            )}

            {(hasServerError.statusCode === 400 || hasServerError.statusCode === 500) && (
                <Toast
                    heading={`${hasServerError?.name}`}
                    type="error"
                    disappearTime={4}
                    isTimer
                    messages={[`${hasServerError?.message} ${t('ADVISOR_ADD_USER_REJECTED_HELPER_MESSAGE')}`]}
                />
            )}

            <RelativeContainer>
                {isFtAdmin && (
                    <StyledBox>
                        <PlanTransferApprovals
                            pendingApprovals={pendingApprovals}
                            approve={approve}
                            reject={reject}
                            isLoading={isLoading}
                            actionLoading={actionLoading}
                            snackbar={snackbar}
                        />
                    </StyledBox>
                )}
                <FinancialAdvisor
                    financialAdvisors={financialUsers}
                    deleteModalState={deleteModalState}
                    advisorSelected={showFinancialAdvisorRemoveModal}
                    columns={columns}
                    handleCloseDeleteModal={handleCloseDeleteModal}
                    handleRemoveFinancialAdvisor={handleRemoveFinancialAdvisor}
                    userRole={userRole}
                    userTenant={userTenant}
                    searchAdvisors={handleTextInput}
                    searchTerm={searchTerm}
                    handleRoleFilterChange={handleRoleFilterChange}
                    filterRole={filterRole}
                    tableLoader={localLoader}
                />
                <ConfirmationCentered
                    title={t('PASSWORD_RESET_CONFIRMATION_TITLE')}
                    content={t('PASSWORD_RESET_CONFIRMATION_MESSAGE')}
                    onConfirmation={() => handleResetPassword(userToResetPassword)}
                    onCancellation={handleCloseResetModal}
                    onCloseIconClick={handleCloseResetModal}
                    open={confirmResetPasswordModalOpen}
                    cancelButtonLabel={t('BUTTON_CANCEL')}
                    confirmButtonLabel={t('CONTINUE_TEXT')}
                    cardMaxWidth="50vw"
                />
                <PaginationContainer>
                    <Pagination
                        onChangePage={handlePageChange}
                        onChangeItemsPerPage={handlePageSizeChange}
                        totalPages={totalPages || 0}
                        currentPage={curentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={totalUserCount || 0}
                    />
                </PaginationContainer>

                <TwoWayTransferPlansModal
                    open={transferModalOpen}
                    onClose={() => setTransferModalOpen(false)}
                    fromUser={{
                        id: selectedUser?.id,
                        name: selectedUser?.userName,
                        email: selectedUser?.userEmail,
                        role: selectedUser?.userType?.toLowerCase()
                    }}
                    users={financialUsers.map((u: any) => ({
                        id: u.id,
                        name: u.userName,
                        email: u.userEmail,
                        role: u.userType?.toLowerCase()
                    }))}
                    refreshApprovals={refreshApprovals}
                />
            </RelativeContainer>
        </>
    );
}
